import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  ArrowLeftIcon,
  Copy,
  Phone,
  Plus,
  ShoppingBag,
  UserIcon,
  XCircle,
} from "@/components/icons";
import {
  Badge,
  Button,
  Skeleton,
  Textarea,
  confirm,
  toast,
} from "@/components/kit";
import { cn } from "@/lib/utils";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { copyToClipboard } from "@/modules/chat-tools/presentation/lib/copy-to-clipboard";
import { ProductFormSectionLike } from "@/modules/orders/presentation/ui/OrderSection";
import {
  ProductPickerModal,
  type PickedVariant,
} from "@/modules/orders/presentation/ui/ProductPickerModal";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";
import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { waitlistLineDtoToFormLine } from "../lib/waitlist-form-lines";
import {
  fetchRestockWaitlistNotifyMessage,
  useCancelRestockWaitlist,
  useNotifyRestockWaitlist,
  useRestockWaitlistQuery,
  useUpdateRestockWaitlist,
} from "../api/queries";
import {
  WaitlistLinesEditor,
  waitlistFormLineFromPick,
  type WaitlistFormLine,
} from "../ui/WaitlistLinesEditor";
import {
  getWaitlistStatusMeta,
  parseImportSnapshot,
  waitlistStatusLabel,
} from "../ui/waitlist-status";

export function RestockWaitlistDetailPage() {
  const { id } = useParams({ from: "/app/restock-waitlist/$id" });
  const nav = useNavigate();
  const canManageWaitlist = useActionPermission(["restock_waitlist:write"]);
  const canCreateOrder = useActionPermission(["orders:write"]);

  const query = useRestockWaitlistQuery(id);
  const notifyEntry = useNotifyRestockWaitlist(id);
  const cancelEntry = useCancelRestockWaitlist(id);
  const updateEntry = useUpdateRestockWaitlist(id);

  const [editLines, setEditLines] = useState<WaitlistFormLine[]>([]);
  const [editNote, setEditNote] = useState("");
  const [editHydrated, setEditHydrated] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  const entry = query.data;
  const pickedVariantIds = useMemo(
    () => new Set(editLines.map((line) => line.variant.variantId)),
    [editLines],
  );

  useEffect(() => {
    if (!entry || editHydrated) return;
    setEditLines(entry.lines.map(waitlistLineDtoToFormLine));
    setEditNote(entry.staffNote ?? "");
    setEditHydrated(true);
  }, [entry, editHydrated]);

  if (query.isLoading) {
    return (
      <>
        <Header />
        <Main className="flex flex-col gap-6">
          <Skeleton className="h-28 rounded-2xl" />
          <Skeleton className="h-48 rounded-2xl" />
        </Main>
      </>
    );
  }

  if (!entry) {
    return (
      <>
        <Header />
        <Main className="flex flex-col gap-6">
          <h2 className="font-display text-2xl">ບໍ່ພົບລາຍການ</h2>
          <Button
            variant="outline"
            className="w-fit rounded-full"
            onClick={() => nav({ to: "/app/restock-waitlist" })}
          >
            <ArrowLeftIcon className="size-4" />
            ກັບ
          </Button>
        </Main>
      </>
    );
  }

  const meta = getWaitlistStatusMeta(entry.status);
  const canCopy = entry.status === "ready" || entry.status === "notified";
  const canCreateOrderFromEntry =
    canCreateOrder &&
    (entry.status === "ready" || entry.status === "notified");

  async function handleCopyMessage() {
    try {
      const { message } = await fetchRestockWaitlistNotifyMessage(id);
      const ok = await copyToClipboard(message);
      if (ok) toast.success("ຄັດລອກແລ້ວ");
      else toast.error("ຄັດລອກບໍ່ສຳເລັດ");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ຄັດລອກບໍ່ສຳເລັດ");
    }
  }

  async function handleNotify() {
    const ok = await confirm({
      title: "ແຈ້ງລູກຄ້າແລ້ວ?",
      description:
        "ລະບົບຈະເລີ່ມ soft hold 3 ວັນ — ກະລຸນາ copy ຂໍ້ຄວາມໄປສົ່ງໃຫ້ລູກຄ້າ",
    });
    if (!ok) return;
    try {
      await notifyEntry.mutateAsync();
      toast.success("ແຈ້ງແລ້ວ · ເລີ່ມ hold 3 ວັນ");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ດຳເນີນການບໍ່ສຳເລັດ");
    }
  }

  async function handleCancel() {
    const ok = await confirm({
      title: "ຍົກເລີກລາຍການ?",
      description: "ລາຍການຈະຖືກຍົກເລີກ ແລະ ປົດຄິວໃຫ້ຄົນຖັດໄປ",
    });
    if (!ok) return;
    try {
      await cancelEntry.mutateAsync({});
      toast.success("ຍົກເລີກລາຍການແລ້ວ");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ຍົກເລີກບໍ່ສຳເລັດ");
    }
  }

  async function handleCreateOrder() {
    const ok = await confirm({
      title: "ສ້າງອໍເດີ້ຈາກລາຍການລໍ?",
      description:
        "ລະບົບຈະ pre-fill ຂໍ້ມູນລູກຄ້າ ແລະ ສິນຄ້າ — ເມື່ອບັນທຶກອໍເດີ້ສຳເລັດ ລາຍການລໍຈະປິດເປັນ converted",
    });
    if (!ok) return;

    nav({
      to: "/app/orders/new",
      search: { waitlistId: entry.id },
    });
  }

  async function handleSaveEdit() {
    if (editLines.length === 0) {
      toast.error("ຕ້ອງມີຢ່າງໜ້ອຍ 1 ລາຍການ");
      return;
    }
    for (const line of editLines) {
      if (!Number.isInteger(line.quantity) || line.quantity < 1) {
        toast.error("ຈຳນວນຕ້ອງເປັນຈຳນວນເຕັມ ≥ 1");
        return;
      }
    }
    try {
      await updateEntry.mutateAsync({
        lines: editLines.map((line) => ({
          variantId: line.variant.variantId,
          quantityRequested: line.quantity,
        })),
        staffNote: editNote.trim() || null,
      });
      toast.success("ບັນທຶກການແກ້ໄຂແລ້ວ");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ແກ້ໄຂບໍ່ສຳເລັດ");
    }
  }

  function handlePickVariant(variant: PickedVariant) {
    if (pickedVariantIds.has(variant.variantId)) {
      toast.error("ສິນຄ້ານີ້ມີໃນລາຍການແລ້ວ");
      return;
    }
    setEditLines((prev) => [...prev, waitlistFormLineFromPick(variant)]);
    setPickerOpen(false);
  }

  return (
    <>
      <Header />
      <Main className="mx-auto flex w-full max-w-3xl flex-col gap-6 pb-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-display text-2xl text-foreground sm:text-3xl">
                {entry.waitNumber}
              </h2>
              <Badge
                variant={meta.variant}
                className={cn("rounded-full", meta.className)}
              >
                {waitlistStatusLabel(entry.status)}
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm">{entry.summaryLabel}</p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          <Button
            variant="outline"
            className="rounded-full border-secondary/40"
            onClick={() => nav({ to: "/app/restock-waitlist" })}
          >
            <ArrowLeftIcon data-icon="inline-start" />
            ກັບ
          </Button>
        </div>

        <section className="brand-card overflow-hidden p-0">
          <div className="bg-linear-to-br from-secondary/15 via-primary/8 to-background px-5 py-5 sm:px-6">
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <div className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-background/80 text-secondary shadow-sm">
                  <UserIcon className="size-5" />
                </div>
                <div>
                  <p className="font-medium text-base">{entry.customerName}</p>
                  {entry.customerPhone ? (
                    <p className="mt-0.5 flex items-center gap-1.5 text-muted-foreground text-sm">
                      <Phone className="size-3.5" />
                      {entry.customerPhone}
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="grid gap-3 text-sm">
                {entry.lines.map((line) => (
                  <div
                    key={line.id}
                    className="rounded-xl border border-border/50 bg-background/60 px-3 py-2.5"
                  >
                    <p className="font-medium">
                      {line.productName} · {line.variantName}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground text-xs">
                      <span>ຈຳນວນ {line.quantityRequested}</span>
                      <span>ສະຕັອກ {line.stockOnHand}</span>
                      {line.queuePosition != null ? (
                        <span>ຄິວ #{line.queuePosition}</span>
                      ) : null}
                    </div>
                    {line.expectedImportSnapshot ? (
                      <p className="mt-1 text-muted-foreground text-xs">
                        {(() => {
                          const snapshot = parseImportSnapshot(
                            line.expectedImportSnapshot,
                          );
                          if (!snapshot) return null;
                          return [
                            snapshot.incomingQuantity != null
                              ? `ນຳເຂົ້າ ${snapshot.incomingQuantity}`
                              : null,
                            snapshot.nearestExpectedAt
                              ? `ຄາດຮອດ ${formatDateLocal(new Date(snapshot.nearestExpectedAt))}`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" · ");
                        })()}
                      </p>
                    ) : null}
                  </div>
                ))}
              </div>
              {entry.holdExpiresAt ? (
                <p className="text-sm">
                  hold ຈົນ:{" "}
                  <span className="font-medium">
                    {formatDateTimeLocal(entry.holdExpiresAt)}
                  </span>
                </p>
              ) : null}
            </div>
          </div>
        </section>

        {entry.status === "waiting" && canManageWaitlist ? (
          <ProductFormSectionLike title="ແກ້ໄຂ">
            <WaitlistLinesEditor
              lines={editLines}
              onAddClick={() => setPickerOpen(true)}
              onRemoveLine={(lineKey) =>
                setEditLines((prev) =>
                  prev.filter((line) => line.lineKey !== lineKey),
                )
              }
              onQuantityChange={(lineKey, quantity) =>
                setEditLines((prev) =>
                  prev.map((line) =>
                    line.lineKey === lineKey ? { ...line, quantity } : line,
                  ),
                )
              }
            />
            <Textarea
              className="min-h-20 rounded-xl"
              value={editNote}
              onChange={(e) => setEditNote(e.target.value)}
              placeholder="ຫມາຍເຫດພະນັກງານ"
            />
            <Button
              className="rounded-full"
              disabled={updateEntry.isPending}
              onClick={handleSaveEdit}
            >
              ບັນທຶກການແກ້ໄຂ
            </Button>
          </ProductFormSectionLike>
        ) : null}

        {entry.staffNote && entry.status !== "waiting" ? (
          <ProductFormSectionLike title="ຫມາຍເຫດພະນັກງານ">
            <p className="text-sm">{entry.staffNote}</p>
          </ProductFormSectionLike>
        ) : null}

        {entry.convertedOrderId ? (
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" className="rounded-full" asChild>
              <Link
                to="/app/orders/$id"
                params={{ id: entry.convertedOrderId }}
              >
                <ShoppingBag data-icon="inline-start" />
                ເບິ່ງອໍເດີ້ທີ່ສ້າງແລ້ວ
              </Link>
            </Button>
          </div>
        ) : null}

        {canManageWaitlist &&
        (entry.status === "waiting" ||
          entry.status === "ready" ||
          entry.status === "notified") ? (
          <div className="flex flex-wrap gap-2">
            {canCopy ? (
              <Button
                variant="outline"
                className="rounded-full"
                onClick={handleCopyMessage}
              >
                <Copy data-icon="inline-start" />
                Copy ຂໍ້ຄວາມ
              </Button>
            ) : null}
            {entry.status === "ready" ? (
              <Button
                className="rounded-full"
                disabled={notifyEntry.isPending}
                onClick={handleNotify}
              >
                ແຈ້ງແລ້ວ
              </Button>
            ) : null}
            {canCreateOrderFromEntry && !entry.convertedOrderId ? (
              <Button
                variant="secondary"
                className="rounded-full"
                onClick={handleCreateOrder}
              >
                <Plus data-icon="inline-start" />
                ສ້າງອໍເດີ້
              </Button>
            ) : null}
            <Button
              variant="destructive"
              className="rounded-full"
              disabled={cancelEntry.isPending}
              onClick={handleCancel}
            >
              <XCircle data-icon="inline-start" />
              ຍົກເລີກ
            </Button>
          </div>
        ) : null}
      </Main>

      <ProductPickerModal
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onPick={handlePickVariant}
        allowZeroStock
        excludeBundles
        subtitle="ເພີ່ມສິນຄ້າໃນລາຍການລໍ"
      />
    </>
  );
}
